// Who can sign in, and where that answer comes from.
//
// The team already keeps the real list: the People at its own org, the one
// marked Internal. This shows the roster that produces rather than asking for it a
// second time. Outside it sits the workspace's allow-list, which Saltbark sets and
// this list can only narrow; that is what stops a typo in a People record from
// admitting a stranger.
//
// This is the ONLY place "which org is us" can be set, and that is what makes it
// safe. As a dropdown on each org it was a per-record flag, so two people could
// mark two orgs in the same minute and nothing could object — and the flag decides
// who can sign in. As one choice here it is exclusive by construction: picking an
// org demotes whichever one held it, in the same append, so the invariant is
// maintained by the writer rather than hoped for by the reader.
//
// It renders the ANSWER and not the rule. A panel that states the rule and leaves
// you to apply it is how an access control quietly stops matching what somebody
// believes it says; the list below is what the server will do, including the people
// it will refuse and why.

import { Link } from 'react-router-dom'

import { Section } from '../components/common'
import { EntityLink } from '../components/EntityLink'
import { TextMenu } from '../components/Picker'
import { normalizeAccess, type Access } from '../lib/model'
import { entitiesOfKind, fieldStr, internalOrgs } from '../lib/reducer'
import { useStore } from '../lib/store'

const ACCESS_NOTE: Record<Access, string> = {
  default: 'can sign in',
  admin: 'can sign in, and change settings',
  none: 'listed here, but cannot sign in',
}

export function TeamPanel() {
  const { state, admin, dispatch } = useStore()
  if (!admin) return null

  const orgs = entitiesOfKind(state, 'org').filter((o) => !o.archived)
  const internal = internalOrgs(state)

  /** One choice, so the demotion of the previous holder rides along with the
   *  promotion. Two events, one append: the log can never show a moment with two. */
  function choose(id: string) {
    const drafts = internal
      .filter((o) => o.id !== id)
      .map((o) => ({ type: 'org.update', subject: o.id, data: { state: 'client' } }))
    if (id) drafts.push({ type: 'org.update', subject: id, data: { state: 'internal' } })
    if (drafts.length > 0) void dispatch(drafts)
  }

  const roster = internal
    .flatMap((org) => org.links)
    .map((id) => state.entities[id])
    .filter((r) => r?.kind === 'person')
    .map((r) => ({ rec: r!, email: fieldStr(r!, 'email').trim() }))
    .sort((a, b) => fieldStr(a.rec, 'name').localeCompare(fieldStr(b.rec, 'name')))

  const canSignIn = roster.filter(
    (r) => r.email && normalizeAccess(r.rec.fields['access']) !== 'none',
  )

  return (
    <Section
      title="Who can sign in"
      note={
        internal.length === 1
          ? `${canSignIn.length} of ${roster.length} people at ${fieldStr(internal[0]!, 'name')}`
          : internal.length === 0
            ? 'no org is marked Internal, so nobody can sign in'
            : `${internal.length} orgs are marked Internal`
      }
    >
      <label className="eyebrow mb-1.5 block">Which of these is us</label>
      <div className="mb-4">
        <TextMenu
          label="Which of these is us"
          chevron
          value={internal[0]?.id ?? ''}
          empty="Nobody"
          onPick={choose}
          options={[
            { key: '', label: 'Nobody' },
            ...orgs.map((o) => ({ key: o.id, label: fieldStr(o, 'name') || 'Untitled' })),
          ]}
        />
      </div>

      {internal.length === 0 ? (
        <p className="note">
          Until one is chosen, nobody can sign in. Choose the org that is you, and the people linked
          to it with an email address become the team.
        </p>
      ) : (
        <>
          {internal.length > 1 ? (
            // Said plainly rather than resolved silently. Everyone at both can sign
            // in, which is almost certainly not what was meant.
            <p className="note mb-3">
              There is meant to be one: the team. Everyone at{' '}
              {internal.map((o) => fieldStr(o, 'name')).join(' and ')} can sign in.
            </p>
          ) : null}

          <div className="rows">
            {roster.map(({ rec, email }) => {
              const access = normalizeAccess(rec.fields['access'])
              const blocked = !email || access === 'none'
              return (
                <div key={rec.id} className="flex flex-wrap items-baseline gap-x-3 py-2">
                  <EntityLink id={rec.id} />
                  <span
                    className="num"
                    style={{ fontSize: 'var(--text-meta)', opacity: email ? 1 : 0.5 }}
                  >
                    {email || 'no email'}
                  </span>
                  <span
                    className="meta"
                    style={{ marginLeft: 'auto', color: blocked ? 'var(--faint)' : undefined }}
                  >
                    {email
                      ? ACCESS_NOTE[access]
                      : 'no email, so nothing to match a sign-in against'}
                  </span>
                </div>
              )
            })}
          </div>

          {roster.length === 0 ? (
            <p className="meta">
              Nobody is linked to{' '}
              <Link to={`/c/${internal[0]!.id}`} className="underline underline-offset-2">
                {fieldStr(internal[0]!, 'name')}
              </Link>{' '}
              yet, so nobody can sign in.
            </p>
          ) : null}
        </>
      )}

      <p className="note mt-4">
        Everyone signs in with an email link. This workspace also has an allow-list of domains that
        may ever hold a seat, set by Saltbark when it was created; this list can only narrow that,
        never widen it, so adding a person here cannot let in an address outside it. Change the
        level on a person's own page.
      </p>
    </Section>
  )
}
